import { useState, useEffect } from 'react'
import { imageService } from '@/utils/imageService'

interface UseAvatarImageOptions {
    avatarId?: string | null
    userId?: string
    enableCache?: boolean
}

interface AvatarImageState {
    src: string | null
    loading: boolean
    error: string | null
}

// Simple in-memory cache for avatar images
const avatarCache = new Map<string, string>()

export const useAvatarImage = (options: UseAvatarImageOptions = {}): AvatarImageState => {
    const { avatarId, enableCache = true } = options
    const [state, setState] = useState<AvatarImageState>({
        src: null,
        loading: false,
        error: null
    })

    useEffect(() => {
        if (!avatarId) {
            setState({ src: null, loading: false, error: null })
            return
        }

        // Check cache first
        if (enableCache && avatarCache.has(avatarId)) {
            setState({
                src: avatarCache.get(avatarId)!,
                loading: false,
                error: null
            })
            return
        }

        let isCancelled = false

        const fetchAvatar = async () => {
            setState(prev => ({ ...prev, loading: true, error: null }))

            try {
                const imageData = await imageService.getImageById(avatarId)

                if (!isCancelled && imageData?.dataUrl) {
                    const avatarSrc = imageData.dataUrl

                    // Cache the result
                    if (enableCache) {
                        avatarCache.set(avatarId, avatarSrc)
                    }

                    setState({
                        src: avatarSrc,
                        loading: false,
                        error: null
                    })
                } else if (!isCancelled) {
                    setState({
                        src: null,
                        loading: false,
                        error: 'Image not found'
                    })
                }
            } catch (error: any) {
                if (!isCancelled) {
                    console.error('Failed to fetch avatar:', error)
                    setState({
                        src: null,
                        loading: false,
                        error: error.message || 'Failed to load avatar'
                    })
                }
            }
        }

        fetchAvatar()

        return () => {
            isCancelled = true
        }
    }, [avatarId, enableCache])

    return state
}

// Utility function to clear avatar cache (useful for when avatars are updated)
export const clearAvatarCache = (avatarId?: string) => {
    if (avatarId) {
        avatarCache.delete(avatarId)
    } else {
        avatarCache.clear()
    }
}

// Preload avatar function for better UX
export const preloadAvatar = async (avatarId: string): Promise<void> => {
    if (!avatarId || avatarCache.has(avatarId)) {
        return
    }

    try {
        const imageData = await imageService.getImageById(avatarId)
        if (imageData?.dataUrl) {
            avatarCache.set(avatarId, imageData.dataUrl)
        }
    } catch (error) {
        console.warn('Failed to preload avatar:', error)
    }
}